import { Button } from "@/components/ui/button";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MapStateProvider } from "@/contexts/MapStateContext";
import { TrackerPosition } from "@/interfaces";
import { formatDateTime } from "@/lib/utils";
import { VehicleInOutMapping } from "@/services/api/trackReports";
import { LockIcon, LockOpen, Map, Power } from "lucide-react";

interface PositionsReportTableProps {
  positions: TrackerPosition[];
  handleMapButtonClick: (position: TrackerPosition) => void;
  inOutMapping: VehicleInOutMapping[];
}

const PositionsReportTable = ({
  positions,
  handleMapButtonClick,
  inOutMapping,
}: PositionsReportTableProps) => {
  const headerMapping: { [key: string]: string } = {};

  Object.values(inOutMapping).forEach((mappingInfo) => {
    headerMapping[mappingInfo.id] = mappingInfo.label;
  });

  const in1Label = headerMapping["in1"] ?? "Entrada 1";
  const in2Label = headerMapping["in2"] ?? "Entrada 2";
  const out1Label = headerMapping["out1"] ?? "Saída 1";
  const out2Label = headerMapping["out2"] ?? "Saída 2";

  const getDriverName = (position: TrackerPosition) => {
    if (position) {
      if (position.driver) {
        return position.driver.name.slice(0, 15);
      }
      if (position.ibutton) {
        return position.ibutton;
      }
    }
    return "";
  };

  let index = 0;

  return (
    <div className="relative overflow-hidden border rounded-md">
      <div className="overflow-auto max-h-[600px]">
        <table className="relative text-sm w-full">
          <TableHeader className="sticky top-0 z-10 bg-background">
            <TableRow>
              <TableHead className="text-center"></TableHead>
              <TableHead className="text-center">Motorista</TableHead>
              <TableHead className="text-center">Hora</TableHead>
              <TableHead className="text-center">Lat</TableHead>
              <TableHead className="text-center">Long</TableHead>
              <TableHead className="text-center">Ignição</TableHead>
              <TableHead className="text-center">Vel.</TableHead>
              <TableHead className="text-center">RPM</TableHead>
              <TableHead className="text-center">Odômetro</TableHead>
              <TableHead className="text-center">Bateria</TableHead>
              <TableHead className="text-center">Bloqueio</TableHead>
              <TableHead className="text-center whitespace-nowrap">
                {out1Label}
              </TableHead>
              <TableHead className="text-center whitespace-nowrap">
                {out2Label}
              </TableHead>
              <TableHead className="text-center whitespace-nowrap">
                {in1Label}
              </TableHead>
              <TableHead className="text-center whitespace-nowrap">
                {in2Label}
              </TableHead>
              <TableHead className="text-center">Evento</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {positions.map((position) => {
              return (
                <TableRow key={index++} className="dark:text-white">
                  <TableCell className="text-center">
                    <Button
                      className="dark:text-white dark:bg-muted p-2"
                      onClick={() => handleMapButtonClick(position)}
                      variant={"ghost"}
                    >
                      <MapStateProvider>
                        <Map className="w-6 h-6" />
                      </MapStateProvider>
                    </Button>
                  </TableCell>
                  <TableCell className="text-center">
                    {getDriverName(position)}
                  </TableCell>
                  <TableCell className="text-center">
                    {formatDateTime(position.date, true)}
                  </TableCell>
                  <TableCell className="text-center">
                    {position.latitude}
                  </TableCell>
                  <TableCell className="text-center">
                    {position.longitude}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-center items-center">
                      {position.ignition ? (
                        <Power className="text-green-500" />
                      ) : (
                        <Power className="text-red-500" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center whitespace-nowrap">
                    {position.speed} km/h
                  </TableCell>
                  <TableCell className="text-center">{position.rpm}</TableCell>
                  <TableCell className="text-center">
                    {position.odometer}
                  </TableCell>
                  <TableCell className="text-center">
                    {position.battery_voltage}v
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-center items-center">
                      {position.blocked ? (
                        <LockIcon className="text-primary-500" />
                      ) : (
                        <LockOpen className="text-primary-500 opacity-30" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">{position.out1}</TableCell>
                  <TableCell className="text-center">{position.out2}</TableCell>
                  <TableCell className="text-center">{position.in1}</TableCell>
                  <TableCell className="text-center">{position.in2}</TableCell>
                  <TableCell className="text-center">
                    {position.event_description ?? "Veiculo em movimento"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </table>
      </div>
    </div>
  );
};

export default PositionsReportTable;
